import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import styles from './UserProfile.module.css';
import { userApi } from '../../services/userApi';
import { useUserContext } from '../../contexts/UserContext';

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  created_at?: string;
  updated_at?: string;
  linkedin?: string;
  profile?: string;
  gender?: string;
  is_scim?: boolean;
  department?: string;
  employee_group?: string;
  verification?: number;
}

interface FormData {
  firstname: string;
  lastname: string;
  title: string;
  mail: string;
  jobtitle: string;
  linkedin: string;
  profile: string;
  gender: string;
  picture?: string;
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('back');
  const { refreshSession } = useUserContext();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    firstname: '',
    lastname: '',
    title: '',
    mail: '',
    jobtitle: '',
    linkedin: '',
    profile: '',
    gender: 'Non-Binary'
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to extract error message
  const getErrorMessage = (error: any): string => {
    if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Fetch current user profile
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const userData = await userApi.getCurrentUserProfile();
      setUser(userData);
      
      // Initialize form data
      setFormData({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        title: userData.title || '',
        mail: userData.mail || '',
        jobtitle: userData.jobtitle || '',
        linkedin: userData.linkedin || '',
        profile: userData.profile || '',
        gender: userData.gender || 'Non-Binary',
        picture: userData.picture || undefined  // Keep existing picture
      });

      // Set profile image preview
      if (userData.picture) {
        setImagePreview(`data:image/png;base64,${userData.picture}`);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to load profile: ${errorMessage}`);
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    const fieldName = id.replace('input', '').toLowerCase();
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      toast.error('Please select a valid image file (JPG or PNG)');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      
      // Convert to base64 (remove data:image/...;base64, prefix)
      const base64 = result.split(',')[1];
      setFormData(prev => ({
        ...prev,
        picture: base64
      }));
    };
    reader.readAsDataURL(file);
  };

  // Handle image deletion
  const handleDeleteImage = () => {
    setImagePreview(null);
    setFormData(prev => ({
      ...prev,
      picture: '' // Empty string to indicate deletion
    }));
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Profile picture removed. Save changes to confirm.');
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstname.trim()) {
      errors.firstname = 'First name is required';
    }

    if (!formData.lastname.trim()) {
      errors.lastname = 'Last name is required';
    }

    if (!formData.mail.trim()) {
      errors.mail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.mail)) {
      errors.mail = 'Please enter a valid email address';
    }

    if (!formData.jobtitle.trim()) {
      errors.jobtitle = 'Job title is required';
    }

    if (!formData.profile.trim()) {
      errors.profile = 'Website profile is required';
    } else if (!/^https?:\/\/.+/.test(formData.profile)) {
      errors.profile = 'Please enter a valid URL starting with http:// or https://';
    }

    if (formData.linkedin && !/^https?:\/\/.+/.test(formData.linkedin)) {
      errors.linkedin = 'Please enter a valid LinkedIn URL starting with http:// or https://';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSaveProfile = async () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    try {
      setSaving(true);

      const updateData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        title: formData.title || null,
        jobtitle: formData.jobtitle,
        linkedin: formData.linkedin || null,
        profile: formData.profile,
        gender: formData.gender,
        picture: formData.picture  // Always include picture to preserve existing image
      };

      await userApi.updateCurrentUserProfile(updateData);
      
      // Refresh the session to update navbar with new user data
      if (refreshSession) {
        await refreshSession();
      }
      
      if (backUrl) {
        toast.success('Profile updated successfully! Redirecting you back...');
        // Kurze Verzögerung, damit der Toast sichtbar ist
        setTimeout(() => {
          navigate(decodeURIComponent(backUrl));
        }, 1500);
      } else {
        toast.success('Profile updated successfully!');
      }
      
      // Refresh user data
      await fetchUserProfile();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to update profile: ${errorMessage}`);
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  // Upload button click handler
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingContainer}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className="container mt-4">


        {user?.is_scim && (
          <div className="row">
            <div className={`alert alert-warning ${styles.scimWarning}`} role="alert">
              This user is managed via SCIM. Some fields cannot be edited and must be changed by the system administrator.
            </div>
          </div>
        )}

        <div className="container-xl px-4 mt-4">
          <div className="row">
            {/* Profile Picture Card */}
            <div className="col-xl-4">
              <div className={`card mb-4 mb-xl-0 ${styles.card}`}>
                <div className={styles.cardHeader}>Profile Picture</div>
                <div className={`card-body text-center ${styles.cardBody}`}>
                  <div className="position-relative d-inline-block mb-2">
                    <img
                      className={`img-account-profile rounded-circle ${styles.profilePicture}`}
                      src={imagePreview || '/static/images/user-profile-icon.png'}
                      alt="Profile Picture"
                    />
                    {/* Löschen-Button nur anzeigen wenn ein echtes Bild vorhanden ist (nicht das Standard-Placeholder) */}
                    {(imagePreview || (user?.picture && user.picture.trim() !== '')) && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm rounded-circle position-absolute shadow-sm"
                        style={{ 
                          top: '5px', 
                          right: '5px',
                          width: '32px',
                          height: '32px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          zIndex: 10
                        }}
                        onClick={handleDeleteImage}
                        title="Remove profile picture"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                          e.currentTarget.style.transition = 'transform 0.2s ease';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <i className="bi bi-x-lg" style={{ fontSize: '14px', fontWeight: 'bold' }}></i>
                      </button>
                    )}
                  </div>
                  <div className={`small font-italic text-muted mb-4 ${styles.profileUploadInfo}`}>
                    JPG or PNG no larger than 5 MB
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/png, image/jpeg"
                    className="d-none"
                    onChange={handleFileUpload}
                  />
                  <button
                    type="button"
                    className={`btn btn-primary ${styles.btn} ${styles.btnPrimary}`}
                    onClick={handleUploadClick}
                  >
                    <i className="bi bi-cloud-upload me-2"></i>
                    Upload new image
                  </button>
                </div>
              </div>
            </div>

            {/* Account Details Card */}
            <div className="col-xl-8">
              <div className={`card mb-4 ${styles.card}`}>
                <div className={styles.cardHeader}>Account Details</div>
                <div className={styles.cardBody}>
                  {/* First and Last Name */}
                  <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputFirstName">
                        First name
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.firstname ? 'is-invalid' : ''}`}
                        id="inputFirstName"
                        type="text"
                        placeholder="Enter first name"
                        value={formData.firstname}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                        required
                      />
                      {validationErrors.firstname && (
                        <div className="invalid-feedback">{validationErrors.firstname}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLastName">
                        Last name
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.lastname ? 'is-invalid' : ''}`}
                        id="inputLastName"
                        type="text"
                        placeholder="Enter last name"
                        value={formData.lastname}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                        required
                      />
                      {validationErrors.lastname && (
                        <div className="invalid-feedback">{validationErrors.lastname}</div>
                      )}
                    </div>
                  </div>

                  {/* Title */}
                  <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputTitle">
                        Title
                      </label>
                      <input
                        className={`form-control ${styles.formControl}`}
                        id="inputTitle"
                        type="text"
                        placeholder="Enter title (e.g. Dr., Prof.)"
                        value={formData.title}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                    <div className="col-md-12">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputMail">
                        Email address
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.mail ? 'is-invalid' : ''}`}
                        id="inputMail"
                        type="email"
                        placeholder="Enter email address"
                        value={formData.mail}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                        required
                      />
                      {validationErrors.mail && (
                        <div className="invalid-feedback">{validationErrors.mail}</div>
                      )}
                    </div>
                  </div>

                  {/* LinkedIn and Profile */}
                  <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputLinkedin">
                        LinkedIn
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.linkedin ? 'is-invalid' : ''}`}
                        id="inputLinkedin"
                        type="url"
                        placeholder="Enter LinkedIn link"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                      />
                      {validationErrors.linkedin && (
                        <div className="invalid-feedback">{validationErrors.linkedin}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputProfile">
                        Website Profile
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.profile ? 'is-invalid' : ''}`}
                        id="inputProfile"
                        type="url"
                        placeholder="Enter profile link"
                        value={formData.profile}
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.profile && (
                        <div className="invalid-feedback">{validationErrors.profile}</div>
                      )}
                    </div>
                  </div>

                  {/* Job Title and Gender */}
                  <div className={`row gx-3 mb-3 ${styles.formRow}`}>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputJobtitle">
                        Job Title
                      </label>
                      <input
                        className={`form-control ${styles.formControl} ${validationErrors.jobtitle ? 'is-invalid' : ''}`}
                        id="inputJobtitle"
                        type="text"
                        placeholder="Enter job title"
                        value={formData.jobtitle}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                        required
                      />
                      {validationErrors.jobtitle && (
                        <div className="invalid-feedback">{validationErrors.jobtitle}</div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className={`small mb-1 ${styles.formLabel}`} htmlFor="inputGender">
                        Gender
                      </label>
                      <select
                        className={`form-control ${styles.formControl}`}
                        id="inputGender"
                        value={formData.gender}
                        onChange={handleInputChange}
                        disabled={user?.is_scim}
                        required
                      >
                        <option value="Non-Binary">Non-Binary</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className={styles.formButtons}>
                    <div className="d-flex gap-2">
                      {backUrl && (
                        <button
                          type="button"
                          className={`btn btn-outline-secondary ${styles.btn}`}
                          onClick={() => navigate(decodeURIComponent(backUrl))}
                        >
                          Cancel & Go Back
                        </button>
                      )}
                      <button
                        type="button"
                        className={`btn btn-primary ${styles.btn} ${styles.btnPrimary}`}
                        onClick={handleSaveProfile}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          backUrl ? 'Save & Go Back' : 'Save changes'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
